<template>
    <!--
        PRODUCT-26110 Phase 2B2: render the native element explicitly instead of
        `<component :is="tag">`. This component is named `Button`, and Vue 3 lets a
        component reference itself by name for recursion, so `:is="'button'"` resolved
        `button` -> capitalize -> `Button` -> THIS component and rendered itself forever
        (stack overflow). Vue 2 rendered the native <button> here. `tag` is only ever
        `button` (default) or `a`.
    -->
    <a
        v-if="tag === 'a'"
        :href="href == null ? 'javascript:void(0)' : href"
        class="Button"
        :class="containerClass"
        v-bind="$attrs"
    >
        <FontAwesomeIcon v-if="loading" class="Button__loadingSpinner" :icon="faSpinner" spin />
        <span v-if="loading && progress != null" class="Button__progress">{{ progress.toFixed() }}%</span>
        <EidIcon v-else-if="icon" :icon-src="icon" v-bind="iconProps" />
        <span>
            <slot />
        </span>
    </a>
    <button
        v-else
        :type="submit ? 'submit' : 'button'"
        :disabled="disabled"
        class="Button"
        :class="containerClass"
        v-bind="$attrs"
    >
        <FontAwesomeIcon v-if="loading" class="Button__loadingSpinner" :icon="faSpinner" spin />
        <span v-if="loading && progress != null" class="Button__progress">{{ progress.toFixed() }}%</span>
        <EidIcon v-else-if="icon" :icon-src="icon" v-bind="iconProps" />
        <span>
            <slot />
        </span>
    </button>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from 'vue-property-decorator';

    import { IconDefinition, faSpinner } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '../EidIcon/EidIcon.vue';

    @Component({
        components: { FontAwesomeIcon, EidIcon },
    })
    export default class Button extends Vue {
        private faSpinner = faSpinner;

        @Prop({ type: String, default: 'button' })
        private tag!: string;

        @Prop({ type: String, default: null })
        private href!: string | null;

        @Prop({ type: String, default: null })
        private type!: string | null;

        @Prop({ type: [ Object, String ], default: null })
        private icon!: IconDefinition | string | null;

        @Prop()
        private iconProps!: any;

        @Prop({ type: Boolean, default: false })
        private submit!: boolean;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Number, default: null })
        private progress!: number | null;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        private hasContent: boolean = false;

        private get typeClassName(): string | null {
            const type = (this.type || '').trim();
            return type ? type.replace(/(^|\s+)/g, (x) => `${x}Button--`) : null;
        }

        private get hasIcon(): boolean {
            return Boolean(this.loading || this.icon);
        }

        private get containerClass(): unknown[] {
            return [ this.typeClassName, {
                'Button--disabled': this.disabled || this.loading,
                'Button--iconOnly': this.hasIcon && !this.hasContent,
            } ];
        }

        private created(): void {
            this.updateContentStatus();
        }

        private beforeUpdate(): void {
            this.updateContentStatus();
        }

        // Vue doesn't have working reactivity on `$slots`, so we can't watch this property easily
        private updateContentStatus(): void {
            this.hasContent = Boolean(this.$slots.default);
        }
    }
</script>
